import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { useState, useCallback, useRef } from "react";
import { CustomDocument } from "@components/ui/documents/CustomDocument";
import { DocumentModal } from "@components/modals/DocumentModal";
import SearchField from "@components/ui/textfields/SearchField";
import {
  Project,
  ProjectDocument,
  ProjectFolder,
  ProjectSubFolder,
  SignatureRequest,
} from "types";

type FolderType = ProjectFolder | ProjectSubFolder;

const FolderItem = ({
  folder,
  parentFolderId,
  depth = 0,
  onDocumentClick,
  hideHidden = false,
  onAddDocument,
  canDrag,
  dragContainerRef,
  disableAddDocument = false,
}: {
  folder: FolderType;
  parentFolderId?: string;
  depth?: number;
  onDocumentClick?: (documentId: string) => void;
  hideHidden?: boolean;
  onAddDocument?: (folderId: string) => void;
  canDrag?: boolean;
  dragContainerRef?: React.RefObject<HTMLDivElement | null>;
  disableAddDocument?: boolean;
}) => {
  // The Décennales tree is managed automatically from partner data — hide
  // the manual "add document" affordance here and for every descendant.
  const isDecennalesBranch = disableAddDocument || folder.name === "Décennales";
  const canAddDocumentHere = !isDecennalesBranch;
  const [isExpanded, setIsExpanded] = useState(true);
  const filteredDocs = (folder.documents || []).filter(
    (doc) => (hideHidden ? !doc.hidden : true),
  );
  const hasDocuments = filteredDocs.length > 0;
  // Walk the whole subtree: a top-level folder whose sub_folders all turn
  // out to be empty should render as "empty" too, otherwise it looks
  // falsely prominent next to its visibly-empty children.
  const subtreeHasAnyDocument = (f: FolderType): boolean => {
    const selfDocs = (f.documents || []).filter((d) => (hideHidden ? !d.hidden : true));
    if (selfDocs.length > 0) return true;
    if ("sub_folders" in f && f.sub_folders) {
      return f.sub_folders.some(subtreeHasAnyDocument);
    }
    return false;
  };
  const hasSubFolders =
    "sub_folders" in folder &&
    folder.sub_folders &&
    folder.sub_folders.length > 0;
  const hasAnyContent = hasDocuments || subtreeHasAnyDocument(folder);
  const indentLeft = depth * 20;

  const folderPath = parentFolderId
    ? `${parentFolderId}/${folder.id}`
    : folder.id;

  return (
    <div className="flex flex-col">
      {/* Folder header — drop target */}
      <div
        className="flex flex-row gap-2 items-center mb-1 group rounded-lg px-2 py-1 transition-all border-2 border-transparent"
        data-drop-folder={folderPath}
        style={{ marginLeft: `${indentLeft}px` }}
        onDragOver={(e) => {
          if (!canDrag) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDragEnter={(e) => {
          if (!canDrag) return;
          e.preventDefault();
          e.stopPropagation();
          // Clear previous hover
          dragContainerRef?.current
            ?.querySelectorAll("[data-drop-folder].drag-hover")
            .forEach((el) => el.classList.remove("drag-hover"));
          // Add hover to this folder
          e.currentTarget.classList.add("drag-hover");
        }}
        onDrop={(e) => {
          if (!canDrag) return;
          e.preventDefault();
          e.stopPropagation();
          // Clean up all drag styles immediately
          const container = dragContainerRef?.current;
          if (container) {
            container.classList.remove("dragging-active");
            container
              .querySelectorAll(".drag-hover")
              .forEach((el) => el.classList.remove("drag-hover"));
          }
          // Dispatch custom event with folder path
          const docId = e.dataTransfer.getData("text/plain");
          if (docId) {
            e.currentTarget.dispatchEvent(
              new CustomEvent("doc-drop", {
                bubbles: true,
                detail: { docId, folderPath },
              }),
            );
          }
        }}
      >
        <div
          className="flex flex-row gap-2 items-center flex-1 cursor-pointer hover:opacity-70 transition-opacity"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon1
            name="folder"
            size={20}
            color={hasAnyContent ? colors.neutral_900 : colors.neutral_300}
          />
          <span
            className="text-sm"
            style={{
              color: hasAnyContent ? colors.neutral_900 : colors.neutral_300,
            }}
          >
            {folder.name}
          </span>
        </div>
        {onAddDocument && canAddDocumentHere && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddDocument(folderPath);
            }}
            className="opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1 px-2 py-1 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 shrink-0"
            title={`Ajouter un document dans ${folder.name}`}
          >
            <Icon1 name="plus" size={10} />
            <span className="text-xs font-medium">Ajouter</span>
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Documents */}
          {hasDocuments && (
            <div className="flex flex-col mb-1 -space-y-0.5">
              {filteredDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex flex-row items-center py-px"
                  style={{ marginLeft: `${indentLeft + 20}px` }}
                >
                  {canDrag && (
                    <div
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", doc.id);
                        // Immediately show all drop targets via DOM
                        const container = dragContainerRef?.current;
                        if (container) {
                          container.classList.add("dragging-active");
                        }
                      }}
                      onDragEnd={() => {
                        const container = dragContainerRef?.current;
                        if (container) {
                          container.classList.remove("dragging-active");
                          container
                            .querySelectorAll(".drag-hover")
                            .forEach((el) => el.classList.remove("drag-hover"));
                        }
                      }}
                      className="flex items-center justify-center w-5 h-5 shrink-0 cursor-grab active:cursor-grabbing select-none"
                      title="Glisser pour déplacer"
                    >
                      <svg width="8" height="14" viewBox="0 0 14 14" fill="currentColor" className="text-neutral-400">
                        <circle cx="4.5" cy="2.5" r="1.5" />
                        <circle cx="9.5" cy="2.5" r="1.5" />
                        <circle cx="4.5" cy="7" r="1.5" />
                        <circle cx="9.5" cy="7" r="1.5" />
                        <circle cx="4.5" cy="11.5" r="1.5" />
                        <circle cx="9.5" cy="11.5" r="1.5" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <CustomDocument
                      name={doc.name}
                      url={doc.file?.url || ""}
                      hidden={doc.hidden}
                      indentLeft={canDrag ? 8 : 20}
                      fontSize={14}
                      onClick={
                        onDocumentClick
                          ? () => onDocumentClick(doc.id)
                          : undefined
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sub-folders */}
          {hasSubFolders &&
            "sub_folders" in folder &&
            folder.sub_folders?.map((subFolder, subIndex) => (
              <FolderItem
                key={subIndex}
                folder={subFolder}
                parentFolderId={folder.id}
                depth={depth + 1}
                onDocumentClick={onDocumentClick}
                hideHidden={hideHidden}
                onAddDocument={onAddDocument}
                canDrag={canDrag}
                dragContainerRef={dragContainerRef}
                disableAddDocument={isDecennalesBranch}
              />
            ))}
        </>
      )}
    </div>
  );
};

/* CSS-driven drag states — injected once */
const dragStyles = `
  .drag-container.dragging-active [data-drop-folder] {
    background-color: rgb(250 250 250);
    border-style: dashed;
    border-color: rgb(212 212 212);
  }
  .drag-container.dragging-active [data-drop-folder]::after {
    content: "Glisser ici";
    font-size: 11px;
    color: rgb(163 163 163);
    margin-left: 8px;
  }
  .drag-container.dragging-active [data-drop-folder].drag-hover {
    background-color: rgb(239 246 255);
    border-style: solid;
    border-color: rgb(96 165 250);
    transform: scale(1.01);
  }
  .drag-container.dragging-active [data-drop-folder].drag-hover::after {
    content: "Déposer ici";
    color: rgb(59 130 246);
    font-weight: 500;
  }
`;

export const DocumentSection = ({
  folders = [],
  activeProject,
  setActiveProject,
  readOnly = false,
  onRequestSignature,
  onAddDocumentToFolder,
  onMoveDocument,
  signatureRequests,
}: {
  folders: (ProjectFolder | ProjectSubFolder)[];
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  readOnly?: boolean;
  onRequestSignature?: (doc: ProjectDocument) => void;
  onAddDocumentToFolder?: (folderId: string) => void;
  onMoveDocument?: (documentId: string, targetFolderPath: string) => void;
  signatureRequests?: SignatureRequest[];
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Native HTML5 drag suppresses mouse-wheel scrolling, so when a document is
  // dragged toward the top/bottom edge of the scroll container we auto-scroll
  // via a requestAnimationFrame loop. Without this the user can't reach folders
  // below/above the fold while dragging.
  const autoScrollRef = useRef<number | null>(null);
  const scrollSpeedRef = useRef(0);

  const stopAutoScroll = useCallback(() => {
    scrollSpeedRef.current = 0;
    if (autoScrollRef.current !== null) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }, []);

  const handleDragAutoScroll = useCallback((e: React.DragEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const EDGE = 60; // px from edge where auto-scroll kicks in
    const MAX_SPEED = 18; // px per frame
    const distTop = e.clientY - rect.top;
    const distBottom = rect.bottom - e.clientY;

    let speed = 0;
    if (distTop < EDGE) {
      speed = -Math.ceil(((EDGE - distTop) / EDGE) * MAX_SPEED);
    } else if (distBottom < EDGE) {
      speed = Math.ceil(((EDGE - distBottom) / EDGE) * MAX_SPEED);
    }
    scrollSpeedRef.current = speed;

    if (speed !== 0 && autoScrollRef.current === null) {
      const tick = () => {
        const node = containerRef.current;
        if (node && scrollSpeedRef.current !== 0) {
          node.scrollTop += scrollSpeedRef.current;
          autoScrollRef.current = requestAnimationFrame(tick);
        } else {
          autoScrollRef.current = null;
        }
      };
      autoScrollRef.current = requestAnimationFrame(tick);
    }
  }, []);

  const normalizedSearch = search.trim().toLowerCase();

  const filterFolders = (
    items: FolderType[],
  ): FolderType[] => {
    if (!normalizedSearch) return items;
    return items
      .map((folder) => {
        const matchingDocs = (folder.documents || []).filter((doc) =>
          doc.name.toLowerCase().includes(normalizedSearch),
        );
        const matchingSubFolders =
          "sub_folders" in folder && folder.sub_folders
            ? filterFolders(folder.sub_folders)
            : [];
        if (matchingDocs.length === 0 && matchingSubFolders.length === 0)
          return null;
        return {
          ...folder,
          documents: matchingDocs,
          ...("sub_folders" in folder ? { sub_folders: matchingSubFolders } : {}),
        } as FolderType;
      })
      .filter(Boolean) as FolderType[];
  };

  const filteredFolders = filterFolders(folders);

  const canDrag = !readOnly && !!onMoveDocument;

  const handleDocDrop = useCallback(
    (e: Event) => {
      const { docId, folderPath } = (e as CustomEvent).detail;
      if (docId && folderPath && onMoveDocument) {
        onMoveDocument(docId, folderPath);
      }
    },
    [onMoveDocument],
  );

  // Listen for custom doc-drop events
  const containerRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      // Cleanup old listener
      if (containerRef.current) {
        containerRef.current.removeEventListener("doc-drop", handleDocDrop);
      }
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (node) {
        node.addEventListener("doc-drop", handleDocDrop);
      }
    },
    [handleDocDrop],
  );

  return (
    <>
      <style>{dragStyles}</style>
      <div
        ref={containerRefCallback}
        className="drag-container flex flex-col max-h-full overflow-auto"
        onDragOver={canDrag ? handleDragAutoScroll : undefined}
        onDragLeave={canDrag ? stopAutoScroll : undefined}
        onDrop={canDrag ? stopAutoScroll : undefined}
        onDragEnd={canDrag ? stopAutoScroll : undefined}
      >
        {selectedDocumentId && activeProject && (
          <DocumentModal
            document_id={selectedDocumentId}
            activeProject={activeProject}
            setActiveProject={setActiveProject}
            onClose={() => setSelectedDocumentId(null)}
            readOnly={readOnly}
            hideSignatures={readOnly}
            onRequestSignature={onRequestSignature}
            signatureRequests={signatureRequests}
          />
        )}

        <div className="mb-4 p-1">
          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Rechercher un document..."
          />
        </div>

        {filteredFolders.map((folder, index) => (
          <FolderItem
            key={index}
            folder={folder}
            depth={0}
            hideHidden={readOnly}
            onDocumentClick={
              activeProject
                ? (docId) => setSelectedDocumentId(docId)
                : undefined
            }
            onAddDocument={!readOnly ? onAddDocumentToFolder : undefined}
            canDrag={canDrag}
            dragContainerRef={containerRef}
          />
        ))}

        {filteredFolders.length === 0 && normalizedSearch && (
          <div className="text-h3 text-neutral-400 text-center py-4">
            Aucun document trouvé
          </div>
        )}
      </div>
    </>
  );
};

/** @deprecated Use CustomDocument directly instead */
export const DocumentItem = ({
  indentLeft,
  doc,
  onPress,
  fontSize = 14,
}: {
  indentLeft: number;
  doc: ProjectDocument;
  onPress?: () => void;
  fontSize?: number;
}) => {
  return (
    <CustomDocument
      name={doc.name}
      url={doc.file?.url || ""}
      hidden={doc.hidden}
      indentLeft={indentLeft}
      fontSize={fontSize}
      onClick={onPress}
    />
  );
};
